import {
    IOutputSensorDataConfigResponse,
    OutputSensorDataConfigCreateRequest,
    TransducerSensitivityFactor,
    TransducerSensitivityInputOutput,
    TransducerSensitivityUnion,
} from '@kcftech/prometheus-client';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { CreateConfigurationForm } from 'pages/Settings/ThirdPartySensorModels/CreateConfigurationModal';

function buildSensitivityRequest(
    data: CreateConfigurationForm,
): TransducerSensitivityUnion {
    if (data.sensitivity.factor !== undefined) {
        return new TransducerSensitivityFactor({
            Sensitivity: data.sensitivity.factor,
        });
    }

    if (
        data.sensitivity.measurementLow !== undefined &&
        data.sensitivity.measurementHigh !== undefined &&
        data.sensitivity.outputLow !== undefined &&
        data.sensitivity.outputHigh !== undefined &&
        data.sensitivity.outputUnits !== undefined
    ) {
        return new TransducerSensitivityInputOutput({
            MeasurementLow: data.sensitivity.measurementLow,
            MeasurementHigh: data.sensitivity.measurementHigh,
            OutputLow: data.sensitivity.outputLow,
            OutputHigh: data.sensitivity.outputHigh,
        });
    }

    throw new Error('Invalid sensitivity');
}

export async function createConfiguration(
    physicalSensorModelId: string,
    corporationId: string | undefined,
    data: CreateConfigurationForm,
) {
    if (!data.dataType) {
        throw new Error('Missing dataType');
    }
    if (!data.units) {
        throw new Error('Missing units');
    }
    if (!data.sensitivity.outputUnits) {
        throw new Error('Missing outputUnits');
    }

    return sdClient.createOutputSensorDataConfig({
        body: new OutputSensorDataConfigCreateRequest({
            PhysicalSensorModelId: physicalSensorModelId,
            CorporationId: corporationId,
            Name: data.displayName,
            DataTypeId: data.dataType.Id,
            OutputSensorCommonType: data.outputDataStreamType?.CommonType,
            OutputSensorRoleCode: data.outputDataStreamType?.RoleCode,
            Unit: data.units.Code,
            TransducerOutputUnit: data.sensitivity.outputUnits,
            TransducerSensitivity: buildSensitivityRequest(data),
            Notes: data.notes,
        }),
    });
}

export function useCreateConfiguration(
    physicalSensorModelId: string,
    corporationId: string | undefined,
    queryOptions?: UseMutationOptions<
        IOutputSensorDataConfigResponse | undefined,
        unknown,
        CreateConfigurationForm
    >,
) {
    return useMutation({
        meta: { name: 'create-configuration' },
        ...queryOptions,
        mutationKey: ['create-configuration'],
        mutationFn: (data: CreateConfigurationForm) =>
            createConfiguration(physicalSensorModelId, corporationId, data),
    });
}
